import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { analytics } from "@/api/endpoints";
import type { Dashboard as DashboardData } from "@/types/api";
import { Badge, Card, EmptyState, Spinner } from "@/components/ui";
import { formatDays, formatNumber } from "@/lib/format";

const EM_DASH = "—";

/* ----------------------------------------------------------------- helpers */

function num(v: unknown): number | null {
  return typeof v === "number" && !Number.isNaN(v) ? v : null;
}

function asArray<T = Record<string, any>>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/* --------------------------------------------------------------- stat card */

interface StatProps {
  label: string;
  value: string;
  sub?: ReactNode;
  hero?: boolean;
}

function Stat({ label, value, sub, hero }: StatProps) {
  return (
    <div
      className={`flex flex-col rounded-lg border bg-white p-5 ${
        hero ? "border-slate-900" : "border-slate-200"
      }`}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span
        className={`mt-2 font-semibold tabular-nums text-slate-900 ${
          hero ? "text-4xl" : "text-3xl"
        }`}
      >
        {value}
      </span>
      {sub && <span className="mt-1 text-xs text-slate-500">{sub}</span>}
    </div>
  );
}

/* ----------------------------------------------------------- chart shells */

function ChartFrame({ children }: { children: ReactNode }) {
  return <div className="h-72 w-full">{children}</div>;
}

/* ------------------------------------------------------------------- page */

export default function Dashboard() {
  const [view, setView] = useState<"deployments" | "gpus">("deployments");

  const { data, isLoading, isError } = useQuery<DashboardData>({
    queryKey: ["dashboard"],
    queryFn: () => analytics.dashboard(),
  });

  const throughput = useMemo(
    () =>
      asArray(data?.throughput).map((t) => ({
        month: String((t as any)?.month ?? ""),
        deployments: num((t as any)?.deployments) ?? 0,
        gpus: num((t as any)?.gpus) ?? 0,
      })),
    [data],
  );

  const capacitySeries = useMemo(
    () =>
      asArray(data?.capacity?.series).map((p) => ({
        date: String((p as any)?.date ?? ""),
        cumulative_gpus: num((p as any)?.cumulative_gpus) ?? 0,
      })),
    [data],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return <EmptyState message="Could not load the velocity dashboard." />;
  }

  /* ---- hero metrics ---- */
  const cycle = data.hero_cycle_time ?? {};
  const cycleMedian = num(cycle.median_days);
  const cycleP90 = num(cycle.p90_days);
  const cycleCount = num(cycle.count);

  const capacity = data.capacity ?? {};
  const doublingDays = num(capacity.doubling_time_days);
  const totalGpus = num(capacity.total_gpus);

  /* ---- bottleneck ---- */
  const perFunnel = data.per_funnel ?? {};
  const binding: string | null =
    typeof data.bottleneck?.binding_constraint === "string"
      ? data.bottleneck.binding_constraint
      : null;
  const combos = num(data.complete_combinations_possible);

  const funnelOrder: Array<{ key: string; label: string }> = [
    { key: "customer", label: "Customer" },
    { key: "hardware", label: "Hardware" },
    { key: "financing", label: "Financing" },
  ];

  /* ---- per-funnel velocity ---- */
  const velocity = asArray(data.per_funnel_velocity);

  const hasThroughput = throughput.some((t) => t.deployments > 0 || t.gpus > 0);
  const hasCapacity = capacitySeries.some((p) => p.cumulative_gpus > 0);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Velocity</h1>
        <p className="text-sm text-slate-500">
          How fast capacity is being deployed.
        </p>
      </div>

      {/* HERO ------------------------------------------------------------- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat
          hero
          label="Deployment cycle time"
          value={formatDays(cycleMedian)}
          sub={
            cycleCount && cycleCount > 0 ? (
              <>
                median · p90 {formatDays(cycleP90)} ·{" "}
                {formatNumber(cycleCount)} deployments
              </>
            ) : (
              "No deployments yet"
            )
          }
        />
        <Stat
          label="Capacity doubling time"
          value={formatDays(doublingDays)}
          sub="doubling time"
        />
        <Stat
          label="Total deployed GPUs"
          value={totalGpus === null ? EM_DASH : formatNumber(totalGpus)}
          sub="across all deployments"
        />
      </div>

      {/* THROUGHPUT + CAPACITY ------------------------------------------- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card
          title="Throughput"
          actions={
            <div className="flex items-center gap-1">
              <button
                onClick={() => setView("deployments")}
                className={`rounded px-2 py-1 text-xs font-medium ${
                  view === "deployments"
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                Deployments
              </button>
              <button
                onClick={() => setView("gpus")}
                className={`rounded px-2 py-1 text-xs font-medium ${
                  view === "gpus"
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                GPUs
              </button>
            </div>
          }
        >
          {hasThroughput ? (
            <ChartFrame>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={throughput}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: "#64748b" }}
                  />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey={view}
                    fill={view === "deployments" ? "#0f172a" : "#7c3aed"}
                    radius={[3, 3, 0, 0]}
                    name={view === "deployments" ? "Deployments" : "GPUs"}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : (
            <EmptyState message="No deployments yet" />
          )}
        </Card>

        <Card title="Capacity growth">
          {hasCapacity ? (
            <ChartFrame>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={capacitySeries}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#64748b" }}
                  />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="cumulative_gpus"
                    stroke="#0f172a"
                    strokeWidth={2}
                    dot={false}
                    name="Cumulative GPUs"
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : (
            <EmptyState message="No capacity history yet" />
          )}
        </Card>
      </div>

      {/* BOTTLENECK ------------------------------------------------------ */}
      <Card title="Deployment readiness">
        <div className="flex flex-col gap-5">
          <div className="rounded-lg bg-slate-900 px-5 py-4 text-white">
            <span className="text-3xl font-semibold tabular-nums">
              {combos === null ? EM_DASH : formatNumber(combos)}
            </span>
            <span className="ml-2 text-sm text-slate-300">
              {combos === 1 ? "deployment" : "deployments"} currently possible
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {funnelOrder.map(({ key, label }) => {
              const f = (perFunnel as Record<string, any>)[key] ?? {};
              const ready = num(f.ready_count);
              const avail = num(f.available_capacity);
              const isBinding = binding === key;
              return (
                <div
                  key={key}
                  className={`rounded-lg border p-4 ${
                    isBinding
                      ? "border-red-300 bg-red-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      {label}
                    </span>
                    {isBinding && <Badge color="red">Bottleneck</Badge>}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                    {ready === null ? EM_DASH : formatNumber(ready)}
                  </div>
                  <div className="text-xs text-slate-500">ready</div>
                  <div className="mt-2 text-xs text-slate-500">
                    Available capacity:{" "}
                    <span className="font-medium text-slate-700">
                      {avail === null ? EM_DASH : formatNumber(avail)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* PER-FUNNEL VELOCITY -------------------------------------------- */}
      <Card title="Per-funnel velocity">
        {velocity.length === 0 ? (
          <EmptyState message="No velocity data yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4 font-medium">Funnel</th>
                  <th className="py-2 pr-4 font-medium">Median cycle</th>
                  <th className="py-2 pr-4 font-medium">p90</th>
                  <th className="py-2 pr-4 font-medium">Throughput</th>
                </tr>
              </thead>
              <tbody>
                {velocity.map((v, i) => {
                  const row = v as Record<string, any>;
                  return (
                    <tr
                      key={String(row.funnel_key ?? row.funnel ?? i)}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="py-2 pr-4 capitalize text-slate-900">
                        {String(row.funnel_label ?? row.funnel ?? row.funnel_key ?? EM_DASH)}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-700">
                        {formatDays(num(row.median_days))}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-700">
                        {formatDays(num(row.p90_days))}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-slate-700">
                        {(() => {
                          const c = num(row.count ?? row.throughput);
                          return c === null ? EM_DASH : formatNumber(c);
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
